import { apiArchivo, apiFetch } from "./client";
import type { RelojPartido } from "../pages/organizer/relojDelPartido";
import type { PhotoCrop } from "../lib/teamPhotoFrame";
import type { CutoutFrame } from "../lib/cutoutFrame";

export type OrganizerCompetitionResponse = {
  leagueId: string;
  leagueName: string | null;
  leagueLogoUrl: string | null;
  leaguePrimaryColor: string | null;
  leagueSecondaryColor: string | null;
  leagueIsPublic: boolean;
  tournaments: Array<{
    id: string;
    name: string;
    season: string;
    statusCode: string;
    /** Código canónico del formato. Usar ESTE para clasificar, no formatLabel. */
    formatCode: string;
    formatLabel: string;
    status: string;
    divisionName?: string | null;
    ageCategoryLabel?: string | null;
    /** Competencia (102): el campeonato paralelo del que cuelga. */
    competitionId?: string | null;
    competitionName?: string | null;
    teamsCount: number;
    matchdaysCount: number;
    matchesTotal: number;
    matchesPlayed: number;
    /** Fase final a mano (154): con qué decide la franja del Inicio si le toca
     *  armarla. Ver `lib/faseFinalPendiente.ts`. */
    groupMatchesTotal?: number;
    groupMatchesResolved?: number;
    playoffRounds?: number;
    playoffMatchesPending?: number;
    finalMatches?: number;
    cupsCount?: number;
    nextMatchLabel: string | null;
  }>;
  teams: Array<{
    id: string;
    name: string;
    logoUrl: string | null;
    categoryLabel: string | null;
    playersCount: number;
    /** Se retiró del torneo: no cuenta para lo que "falta hacer". */
    withdrawn?: boolean;
  }>;
  activeTournament: {
    id: string;
    name: string;
    season: string;
    formatLabel: string;
    status: string;
    statusCode: string;
    teamsCount: number;
    matchdaysCount: number;
    currentMatchdayLabel: string | null;
  } | null;
};

export type TournamentScheduleConfig = {
  weekdays: number[];
  startTimes: string[];
  venueIds: string[];
};

export type TournamentDetail = {
  id: string;
  name: string;
  season: string;
  formatCode: string;
  statusCode: string;
  status: string;
  classifyTopN?: number | null;
  yellowCardsForSuspension?: number | null;
  redCardSuspensionMatches?: number | null;
  /** Marcador del W.O. (168): goles del que sí se presentó. null = 3 (3-0). */
  walkoverGoals?: number | null;
  /** Lista de buena fe (080): cupo y último día para fichar. */
  rosterMaxPlayers?: number | null;
  rosterLockDate?: string | null;
  /** Orden de desempate ya parseado (081). Vacío = el histórico. */
  tieBreakRules?: string[];
  matchFormat?: string | null;
  periodCount?: number | null;
  periodMinutes?: number | null;
  isDoubleRound?: boolean;
  includeThirdPlace?: boolean;
  pointsWin?: number;
  pointsDraw?: number;
  pointsLoss?: number;
  scheduleConfig?: TournamentScheduleConfig | null;
  leagueId: string;
  divisionId?: string | null;
  divisionName?: string | null;
  ageCategoryLabel?: string | null;
  /** Competencia (102): el campeonato paralelo de la liga. */
  competitionId?: string | null;
  competitionName?: string | null;
  promoteTopN?: number | null;
  relegateBottomN?: number | null;
  promotesToTournamentId?: string | null;
  promotesToTournamentName?: string | null;
  relegatesToTournamentId?: string | null;
  relegatesToTournamentName?: string | null;
  teamsCount: number;
  matchesPlayed: number;
  matchesTotal: number;
  // Conteos reales que devuelve getTournamentDetail (backend). scheduledMatches
  // cuenta partidos aún no finalizados (scheduled/confirmed/in_progress/paused/
  // pending_report/suspended); pendingResults cuenta los que esperan carga.
  scheduledMatches?: number;
  pendingResults?: number;
  teams: Array<{
    id: string;
    name: string;
    logoUrl: string | null;
  }>;
  // Otros campos posibles segun el backend (matchdays, etc.)
  [key: string]: unknown;
};

export type OrganizerMatchRow = {
  id: string;
  tournamentId: string;
  matchdayId: string | null;
  matchdayLabel: string | null;
  matchdaySequence?: number | null;
  /**
   * Día de la JORNADA (YYYY-MM-DD). Es lo que se muestra cuando el partido
   * todavía no tiene hora: "Fecha 5 · viernes 3, horario a confirmar".
   */
  matchdayStartsOn?: string | null;
  matchdayEndsOn?: string | null;
  /** La fecha está oculta a los jugadores. Sólo llega en el listado del organizador. */
  matchdayHidden?: boolean;
  /** Comentario del organizador que VE EL JUGADOR ("suspendido por lluvia"). */
  publicNote?: string | null;
  groupId: string | null;
  groupName: string | null;
  stageCode?: string;
  /** Regla del amistoso de libres (stage 'bye'): 'double_win' (o null) / 'real_result'. */
  byeMode?: string | null;
  homeTeamId: string;
  homeTeamName: string;
  homeTeamLogoUrl: string | null;
  awayTeamId: string;
  awayTeamName: string;
  awayTeamLogoUrl: string | null;
  scheduledAt: string | null;
  status: string;
  scheduleLocked?: boolean;
  homeScore: number | null;
  awayScore: number | null;
  homeScorePenalties?: number | null;
  awayScorePenalties?: number | null;
  venueId: string | null;
  venueName: string | null;
  /**
   * POR QUE es W.O.: "no_show" (planton) o "withdrawal" (el equipo se retiro del
   * torneo). Sin este dato las dos cosas se veian igual, y no son lo mismo: a
   * uno no lo esperaron, el otro ya no existe.
   */
  walkoverReason?: string | null;
  /** Jugadores sancionados que se pierden este partido (motor disciplinario). */
  suspendedPlayers?: MatchSuspendedPlayer[];
  /**
   * Programado con la hora vencida (+3 h) y sin resultado, calculado por el
   * backend con el MISMO corte que el banner de Torneo y los baldes de la app.
   * No recalcularlo acá: `scheduledAt` es hora de pared y compararlo contra
   * `Date.now()` corre el resultado tres horas.
   */
  overdue?: boolean;
  /**
   * Minuto corrido y tiempo del partido EN CURSO (null fuera de juego). Es lo
   * que convierte "EN VIVO" en un dato accionable —recién arrancó o está por
   * terminar—, igual que en la tarjeta de Android.
   */
  liveMinute?: number | null;
  livePeriod?: number | null;
};

export type MatchSuspendedPlayer = {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  reason: string | null;
  originKind: string | null;
};

export type StandingsRow = {
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  position: number;
  groupId?: string | null;
  groupName?: string | null;
  recentResults?: string[]; // ["W","D","L"...]
  /** Estado de inscripción: "withdrawn" = retirado (se marca en la tabla). */
  entryStatus?: string | null;
};

export type StandingsGroup = {
  id: string | null;
  name: string | null;
  rows: StandingsRow[];
};

export type StandingsResponse = {
  tournamentName: string;
  qualifyCount?: number | null;
  promoteCount?: number | null;
  relegateCount?: number | null;
  hasGroups?: boolean;
  groups?: StandingsGroup[];
  rows: StandingsRow[];
  /** Orden de desempate en castellano (describirDesempate del backend). */
  tieBreak?: Array<{ key: string; label: string }>;
  /**
   * TABLA ANUAL (099): la suma con los otros torneos de la temporada. Viaja
   * DENTRO de la tabla — es la misma tabla vista de otra forma — así que la
   * pantalla no necesita una segunda llamada para saber si existe.
   * null = este torneo no suma con ninguno.
   */
  annual?: AnnualView | null;
  /**
   * EL CAMPEÓN DEL TORNEO (no el de la temporada, que va en `annual`). Viaja
   * con la tabla porque es donde se lee: arriba de la tabla final. null hasta
   * que el organizador finaliza el torneo — el candado es `campeonAnunciable`
   * en el backend, y proclamarlo es justamente el gesto de cerrarlo.
   */
  champion?: { teamId: string; teamName: string; logoUrl: string | null; reason: string } | null;
};

export async function fetchOrganizerCompetition(): Promise<OrganizerCompetitionResponse> {
  return apiFetch<OrganizerCompetitionResponse>("/api/v1/organizer/competition");
}

export async function fetchTournamentDetail(tournamentId: string): Promise<TournamentDetail> {
  return apiFetch<TournamentDetail>(
    `/api/v1/organizer/tournaments/${tournamentId}/detail`,
  );
}

export type OrganizerProfile = {
  displayName: string;
  roleTitle: string;
  /** Foto de la cuenta (la misma de la app). null = todavía no puso una. */
  avatarUrl?: string | null;
  email: string;
  phone: string;
  organizationName: string;
};

/**
 * El link de pago pendiente de la liga (lo que FORJA le cobra por la
 * temporada), para el «Pagar» del cajón. null = no hay, o el servidor lo
 * apagó (PAGAR_EN_APP=off). El pago pasa en /pagar/<token>, afuera de la app.
 */
export type PagoPendiente = {
  url: string;
  concepto: string;
  monto: number;
  moneda: string;
  pendientes: number;
};

export async function fetchPagoPendiente(leagueId: string): Promise<PagoPendiente | null> {
  const r = await apiFetch<{ pago: PagoPendiente | null }>(
    `/api/v1/organizer/leagues/${encodeURIComponent(leagueId)}/pago-pendiente`,
  );
  return r.pago;
}

export async function fetchOrganizerProfile(): Promise<OrganizerProfile> {
  return apiFetch<OrganizerProfile>("/api/v1/organizer/profile");
}

/** Actualiza los datos editables del perfil (nombre visible y teléfono). */
export async function updateOrganizerProfile(input: {
  displayName?: string;
  phone?: string | null;
}): Promise<OrganizerProfile> {
  return apiFetch<OrganizerProfile>("/api/v1/organizer/profile", {
    method: "PUT",
    body: input,
  });
}

/**
 * Endpoint nuevo de Sprint 2.5: lista los partidos de un torneo específico,
 * ordenados por fecha. Shape limpio para el panel web (incluye tournamentId,
 * matchdayId, etc.).
 */
export async function fetchTournamentMatches(
  tournamentId: string,
): Promise<{ matches: OrganizerMatchRow[] }> {
  return apiFetch<{ matches: OrganizerMatchRow[] }>(
    `/api/v1/organizer/tournaments/${tournamentId}/matches`,
  );
}

export async function fetchStandings(tournamentId: string): Promise<StandingsResponse> {
  return apiFetch<StandingsResponse>(
    `/api/v1/organizer/tournaments/${tournamentId}/standings`,
  );
}

// ===================== COMPETENCIAS (102) =====================
// Los campeonatos paralelos de una liga: "Sábados Cat E+", "Masculino
// Viernes"… Cada torneo cuelga de una (o de ninguna: queda "sin agrupar").

export type Competition = {
  id: string;
  name: string;
  sortOrder: number;
  tournamentsCount: number;
  /** Temporadas distintas, de la más nueva a la más vieja. */
  seasons: string[];
  lastTournamentName: string | null;
};

export async function fetchCompetitions(leagueId: string): Promise<{ competitions: Competition[] }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/competitions`);
}

export async function createCompetition(
  leagueId: string,
  name: string,
): Promise<{ competition: Competition; competitions: Competition[] }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/competitions`, { method: "POST", body: { name } });
}

export async function renameCompetition(competitionId: string, name: string): Promise<{ id: string; name: string }> {
  return apiFetch(`/api/v1/organizer/competitions/${competitionId}`, { method: "PUT", body: { name } });
}

export async function deleteCompetition(competitionId: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/api/v1/organizer/competitions/${competitionId}`, { method: "DELETE" });
}

/** null = sacar el torneo de su competencia. */
export async function setTournamentCompetition(
  tournamentId: string,
  competitionId: string | null,
): Promise<{ tournamentId: string; competitionId: string | null }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/competition`, {
    method: "PUT",
    body: { competitionId },
  });
}

// ===================== TABLA ANUAL (099) =====================
// Varios torneos de la temporada (Apertura + Intermedio + Clausura) sumando
// en una sola tabla. La arma el organizador eligiendo cuáles suman.

export type AnnualTournamentRef = {
  id: string;
  name: string;
  season: string;
  status: string;
  /** Ya tiene tabla: se pueden sembrar series por su posición. */
  hasStandings?: boolean;
  /** Aporta puntos sin ser de esta anual (143): otra divisional también lo suma. */
  shared?: boolean;
};

/** De dónde salen los ascensos y descensos: la Anual, el promedio o la tabla de cada torneo (143). */
export type RelegationMode = "annual" | "average" | "tournament";

/**
 * Trae las mismas filas que una tabla normal (se dibuja con el mismo
 * componente). No se define como `StandingsResponse & {...}` porque la propia
 * StandingsResponse la incluye: sería un tipo que se referencia a sí mismo.
 */
export type AnnualView = {
  annualTableId: string;
  name: string;
  tournaments: AnnualTournamentRef[];
  /** Alias de `name`, para dibujarla con el mismo componente. */
  tournamentName: string;
  hasGroups?: boolean;
  groups?: StandingsGroup[];
  rows: StandingsRow[];
  tieBreak?: Array<{ key: string; label: string }>;
  /** Campeón de la temporada (100), cuando ya salió. */
  champion?: { teamId: string; teamName: string; logoUrl: string | null; reason: string } | null;
  /** El torneo "Definición", si ya se armó. */
  definition?: { tournamentId: string; name: string; status: string } | null;
  /** Zonas de ascenso/descenso de la temporada (la tabla del descenso). */
  zonas?: {
    promoteCount: number | null;
    promotesToName: string | null;
    relegateCount: number | null;
    relegatesToName: string | null;
  } | null;
  /** Cómo se decide el descenso de esta temporada (101). */
  relegation?: {
    mode: RelegationMode;
    averageSeasons: number;
    previousAnnualTableId: string | null;
    previousAnnualName: string | null;
  };
  /** Tabla de promedios, sólo cuando la temporada desciende por promedio. */
  averages?: {
    seasons: Array<{ annualTableId: string; name: string }>;
    rows: Array<{
      teamId: string;
      teamName: string;
      logoUrl: string | null;
      points: number;
      played: number;
      average: number;
      position: number;
      porTemporada: Array<{ annualTableId: string; name: string; points: number; played: number }>;
    }>;
  } | null;
};

/** Las otras temporadas de la liga, para elegir con cuál promedia. */
export type AnnualSeasonRef = { annualTableId: string; name: string };

export async function setRelegationMode(
  tournamentId: string,
  body: {
    mode: RelegationMode;
    averageSeasons?: number | null;
    previousAnnualTableId?: string | null;
  },
): Promise<{ annual: AnnualView | null; seasons: AnnualSeasonRef[] }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/annual/relegation`, {
    method: "PUT",
    body,
  });
}

// ===================== DEFINICIÓN DEL CAMPEONATO (100) =====================
// Campeón del Apertura vs campeón del Clausura; si la semi la gana el líder de
// la Anual es campeón, si no hay final contra él.

export type DefinitionTeam = { teamId: string; teamName: string; logoUrl: string | null };

export type DefinitionPreview = {
  annualTableId: string;
  annualName: string;
  torneos: Array<{ id: string; name: string; status: string; completo: boolean }>;
  aperturaId: string | null;
  clausuraId: string | null;
  campeonApertura: DefinitionTeam | null;
  campeonClausura: DefinitionTeam | null;
  lider: DefinitionTeam | null;
  caso: "campeon_directo" | "semifinal" | null;
  /** El reglamento aplicado a ESTOS equipos, en castellano. */
  explicacion: string[];
  ready: boolean;
  blockers: string[];
  definition: { tournamentId: string; name: string; status: string } | null;
  campeon: (DefinitionTeam & { reason: string }) | null;
};

export async function fetchDefinition(
  tournamentId: string,
  eleccion: { aperturaId?: string | null; clausuraId?: string | null } = {},
): Promise<{ preview: DefinitionPreview | null }> {
  const q = new URLSearchParams();
  if (eleccion.aperturaId) q.set("aperturaId", eleccion.aperturaId);
  if (eleccion.clausuraId) q.set("clausuraId", eleccion.clausuraId);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/definition${suffix}`);
}

export async function createDefinition(
  tournamentId: string,
  body: { aperturaId?: string | null; clausuraId?: string | null; semiLegs: 1 | 2; finalLegs: 1 | 2 },
): Promise<{
  campeonDirecto: boolean;
  definitionTournamentId: string | null;
  name: string | null;
  preview: DefinitionPreview;
}> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/definition`, { method: "POST", body });
}

export type AnnualCandidate = AnnualTournamentRef & {
  teamCount: number;
  /** Equipos que comparte con el torneo consultado. */
  commonTeams: number;
  annualTableId: string | null;
};

export type AnnualResponse = {
  annual: AnnualView | null;
  candidates: AnnualCandidate[];
  /** Otras temporadas de la liga (para promediar el descenso). */
  seasons?: AnnualSeasonRef[];
};

export async function fetchTournamentAnnual(tournamentId: string): Promise<AnnualResponse> {
  return apiFetch<AnnualResponse>(`/api/v1/organizer/tournaments/${tournamentId}/annual`);
}

/** Lista vacía = este torneo deja de sumar en la anual. */
export async function setTournamentAnnual(
  tournamentId: string,
  tournamentIds: string[],
): Promise<AnnualResponse> {
  return apiFetch<AnnualResponse>(`/api/v1/organizer/tournaments/${tournamentId}/annual`, {
    method: "PUT",
    body: { tournamentIds },
  });
}

export type AddTeamsToTournamentResult = {
  createdInLeague: number;
  reusedFromLeague: number;
  associatedToTournament: number;
};

/**
 * Disponibilidad de un equipo en un torneo (restricciones de programación).
 * Arreglo vacío = sin límite en esa dimensión. Semántica OBLIGATORIA.
 */
export type TeamSchedulingPrefs = {
  allowedWeekdays: number[]; // 0=domingo … 6=sábado
  allowedTimes: string[]; // "HH:MM"
  allowedVenueIds: string[];
};

export type TournamentTeamRow = {
  id: string;
  name: string;
  shortName: string | null;
  logoUrl: string | null;
  photoUrl: string | null;
  photoAssetId?: string | null;
  /** Foto original y encuadre de la portada (117). Sin ellos, la foto se reacomoda desde sí misma. */
  photoSourceAssetId?: string | null;
  photoSourceUrl?: string | null;
  photoCrop?: PhotoCrop | null;
  entryStatus: string;
  /** Partidos del equipo en el torneo (para el subtítulo de la ficha). */
  matchesCount: number;
  /** Plantel de ESTE torneo y cuántos tienen cuenta (sin cuenta no hay avisos). */
  playersCount?: number;
  registeredCount?: number;
  schedulingPrefs: TeamSchedulingPrefs;
  /** Grupo del equipo en torneos con grupos (null en round-robin simple). */
  groupId?: string | null;
  groupName?: string | null;
};

export async function fetchTournamentTeams(
  tournamentId: string,
): Promise<{ teams: TournamentTeamRow[] }> {
  return apiFetch<{ teams: TournamentTeamRow[] }>(
    `/api/v1/organizer/tournaments/${tournamentId}/teams`,
  );
}

// ============ SPRINT 3 ============

export type CreateTournamentInput = {
  name: string;
  season: string;
  formatCode: "round_robin" | "groups_playoffs" | "knockout";
  groupCount?: number;
  pointsWin?: number;
  pointsDraw?: number;
  pointsLoss?: number;
  divisionId?: string | null;
  ageCategoryLabel?: string | null;
  /** Competencia (102): de qué campeonato paralelo de la liga es este torneo. */
  competitionId?: string | null;
  promoteTopN?: number | null;
  relegateBottomN?: number | null;
  promotesToTournamentId?: string | null;
  relegatesToTournamentId?: string | null;
  matchFormat?: MatchFormat | null;
  periodCount?: number | null;
  periodMinutes?: number | null;
  classifyTopN?: number | null;
  yellowCardsForSuspension?: number | null;
  redCardSuspensionMatches?: number | null;
  isDoubleRound?: boolean;
  includeThirdPlace?: boolean;
  /** Amistoso de libres automático (grupos): null/ausente = apagado. */
  byeFriendlies?: string | null;
  /** Orden de desempate. Ausente = la cadena regional (diferencia de gol primero). */
  tieBreakRules?: string[];
  scheduleConfig?: TournamentScheduleConfig | null;
};

/**
 * PRESETS DE DESEMPATE — las dos escuelas que existen de verdad.
 *
 * `regional` es el default y el que usa la región: AUF, AFA, CONMEBOL, FIFA en
 * fase de grupos, Premier, Bundesliga. `directo` es LaLiga y la Serie A, y era
 * nuestro default por herencia del código viejo, no por decisión de nadie.
 *
 * Son dos atajos, no una jaula: después de creado el torneo, el orden completo
 * se arma criterio por criterio desde abajo de la tabla (TieBreakEditor), que
 * es donde se ve su efecto.
 */
export const PRESETS_DESEMPATE = {
  regional: {
    rules: ["goal_difference", "goals_for", "head_to_head"],
    label: "Diferencia de gol",
    // `detail` va en una línea que TRUNCA (el selector es angosto): tiene que
    // entrar entera o se corta a media palabra. La cadena completa se muestra
    // en el resumen, que ocupa la fila entera.
    detail: "Como AUF, AFA y CONMEBOL",
    chain: "Diferencia de gol → goles a favor → enfrentamiento directo",
  },
  directo: {
    rules: ["head_to_head", "goal_difference", "goals_for"],
    label: "Enfrentamiento directo",
    detail: "Como LaLiga y la Serie A",
    chain: "Enfrentamiento directo → diferencia de gol → goles a favor",
  },
} as const;

export type PresetDesempate = keyof typeof PRESETS_DESEMPATE;

// Modalidad de cancha → estructura del partido (tiempos + duración por defecto).
// Se guardan los números en el torneo, así son editables y no derivados.
export type MatchFormat = "F5" | "F7" | "F8" | "F11" | "futsal";

export const MATCH_FORMATS: Record<MatchFormat, { label: string; periods: number; minutes: number }> = {
  F5: { label: "Fútbol 5", periods: 2, minutes: 20 },
  F7: { label: "Fútbol 7", periods: 2, minutes: 25 },
  F8: { label: "Fútbol 8", periods: 2, minutes: 25 },
  F11: { label: "Fútbol 11", periods: 2, minutes: 45 },
  futsal: { label: "Futsal", periods: 2, minutes: 20 },
};

/** "2 tiempos de 45 min" a partir de los campos del torneo (null si no hay dato). */
export function formatMatchStructure(
  matchFormat: string | null | undefined,
  periodCount: number | null | undefined,
  periodMinutes: number | null | undefined,
): string | null {
  const periods = periodCount ?? (matchFormat && matchFormat in MATCH_FORMATS ? MATCH_FORMATS[matchFormat as MatchFormat].periods : null);
  const minutes = periodMinutes ?? (matchFormat && matchFormat in MATCH_FORMATS ? MATCH_FORMATS[matchFormat as MatchFormat].minutes : null);
  if (!periods || !minutes) return null;
  return `${periods} tiempos de ${minutes} min`;
}

/** Duración total del partido en minutos (tiempos × min), o null si no se definió. */
export function matchDurationMinutes(
  matchFormat: string | null | undefined,
  periodCount: number | null | undefined,
  periodMinutes: number | null | undefined,
): number | null {
  const periods = periodCount ?? (matchFormat && matchFormat in MATCH_FORMATS ? MATCH_FORMATS[matchFormat as MatchFormat].periods : null);
  const minutes = periodMinutes ?? (matchFormat && matchFormat in MATCH_FORMATS ? MATCH_FORMATS[matchFormat as MatchFormat].minutes : null);
  if (!periods || !minutes) return null;
  return periods * minutes;
}

export async function createTournament(
  leagueId: string,
  body: CreateTournamentInput,
): Promise<{ id: string; name: string; season: string; formatCode: string; status: string }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/tournaments`, {
    method: "POST",
    body,
  });
}

/**
 * Crea una liga nueva y devuelve su id. El plan por defecto es "free".
 *
 * La zona horaria es OBLIGATORIA: de ella el backend deduce el país de la liga
 * (`leagues.country_code`), que decide la hora de los partidos y cómo se le
 * cobra. Con un default en Uruguay, dos de las tres puertas de alta se
 * olvidaban de pedirla y toda liga argentina nacía uruguaya.
 */
export async function createLeague(
  name: string,
  /** Zona horaria IANA del país elegido (ver `lib/paisesDeLaLiga.ts`). */
  timezone: string,
):Promise<{ league: { id: string; name: string; slug: string }; plan: string }> {
  return apiFetch("/api/v1/organizer/leagues", {
    method: "POST",
    body: {
      name,
      timezone,
      planCode: "free",
    },
  });
}

/** Elimina una liga (el backend sólo permite si no tiene torneos). */
export async function deleteLeague(leagueId: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}`, { method: "DELETE" });
}

export async function updateTournament(
  tournamentId: string,
  body: {
    name?: string;
    season?: string;
    /** Categoría ("Libre", "+30", "Sub-15"): label libre, "" la vacía. */
    ageCategoryLabel?: string | null;
    status?: string;
    formatCode?: string;
    pointsWin?: number;
    pointsDraw?: number;
    pointsLoss?: number;
    includeThirdPlace?: boolean;
    classifyTopN?: number | null;
    /** Cadena de ascensos/descensos (060). 0/null apaga la regla. */
    promoteTopN?: number | null;
    relegateBottomN?: number | null;
    promotesToTournamentId?: string | null;
    relegatesToTournamentId?: string | null;
    yellowCardsForSuspension?: number | null;
    redCardSuspensionMatches?: number | null;
    /** Marcador del W.O. (168). 0/null = el default (3-0); 1..10 lo fija. */
    walkoverGoals?: number | null;
    /** Lista de buena fe (080): cupo por equipo y último día para fichar. */
    rosterMaxPlayers?: number | null;
    rosterLockDate?: string | null;
    matchFormat?: MatchFormat | null;
    periodCount?: number | null;
    periodMinutes?: number | null;
    isDoubleRound?: boolean;
    /** Amistoso de libres automático (grupos): null lo apaga. */
    byeFriendlies?: "double_win" | "real_result" | null;
    scheduleConfig?: TournamentScheduleConfig | null;
  },
): Promise<{ updated: boolean }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}`, {
    method: "PUT",
    body,
  });
}

export async function deleteTournament(
  tournamentId: string,
): Promise<{ deleted: boolean }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}`, {
    method: "DELETE",
  });
}

export async function deleteFixture(
  tournamentId: string,
  opts?: {
    /** true = borra también los grupos y la asignación A/B (default: se conservan). */
    borrarGrupos?: boolean;
    /** true = reconoce que hay resultados cargados y se pierden; sin esto,
     *  con partidos resueltos el backend rebota (409 fixture_has_played). */
    confirmarJugados?: boolean;
  },
): Promise<{ deletedMatches: number; deletedMatchdays: number }> {
  const params = new URLSearchParams();
  if (opts?.borrarGrupos) params.set("grupos", "borrar");
  if (opts?.confirmarJugados) params.set("jugados", "confirmar");
  const query = params.size > 0 ? `?${params.toString()}` : "";
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/fixture${query}`, {
    method: "DELETE",
  });
}

export async function updateTeam(
  tournamentId: string,
  teamId: string,
  body: {
    name?: string;
    shortName?: string | null;
    logoAssetId?: string | null;
    photoAssetId?: string | null;
    /** Sólo junto con photoAssetId: la original y el área elegida. */
    photoSourceAssetId?: string | null;
    photoCrop?: PhotoCrop | null;
    schedulingPrefs?: TeamSchedulingPrefs;
  },
): Promise<{ ok: boolean }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}`,
    { method: "PUT", body },
  );
}

export async function removeTeamFromTournament(
  tournamentId: string,
  teamId: string,
): Promise<{ removed: boolean }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}`,
    { method: "DELETE" },
  );
}

// ============== Novedades ==============
export type NewsType = "info" | "warning" | "success";
export type NewsStatus = "draft" | "published" | "archived";

export type NewsItem = {
  id: string;
  title: string;
  body: string;
  type: NewsType;
  status: NewsStatus;
  tournamentId: string | null;
  publishedAt: string | null;
  createdAt: string;
};

export async function fetchLeagueNews(
  leagueId: string,
): Promise<{ news: NewsItem[] }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/news`);
}

export async function createNews(
  leagueId: string,
  body: {
    title: string;
    body: string;
    type: NewsType;
    status: "draft" | "published";
    tournamentId?: string | null;
    /** ¿Les suena el teléfono? (122) Sin esto la novedad sólo va a la campana. */
    notifyPush?: boolean;
  },
): Promise<{ id: string }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/news`, {
    method: "POST",
    body,
  });
}

export async function updateNews(
  newsId: string,
  body: {
    title: string;
    body: string;
    type: NewsType;
    status: NewsStatus;
    notifyPush?: boolean;
  },
): Promise<{ updated: boolean }> {
  return apiFetch(`/api/v1/organizer/news/${newsId}`, { method: "PUT", body });
}

/**
 * A cuánta gente le llega esta novedad y cuántas pushes le quedan a la liga en
 * la semana. Se consulta MIENTRAS se redacta: el número a la vista es lo que
 * hace pensar dos veces antes de hacer vibrar 200 teléfonos.
 */
export type NewsAudience = {
  destinatarios: number;
  pushRestantes: number;
  topeSemanal: number;
};

export async function fetchNewsAudience(
  leagueId: string,
  tournamentId: string | null,
): Promise<NewsAudience> {
  const qs = tournamentId ? `?tournamentId=${tournamentId}` : "";
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/news/alcance${qs}`);
}

export async function deleteNews(
  newsId: string,
): Promise<{ deleted: boolean }> {
  return apiFetch(`/api/v1/organizer/news/${newsId}`, { method: "DELETE" });
}

export type MatchEventRow = {
  id: string;
  matchId: string;
  eventCode: string;
  teamId: string | null;
  teamName: string | null;
  playerId: string | null;
  playerName: string | null;
  /** CAMBIO: quién entra y quién sale, por id. Null en el resto de los eventos.
   *  Es lo que permite derivar el EN CANCHA (titulares ± cambios − rojas); el
   *  feed traía el cambio con playerId null y los nombres pegados. */
  playerInId: string | null;
  playerOutId: string | null;
  minute: number | null;
  periodCode: string | null;
  sequenceInMatch: number;
  /**
   * Con qué id lo mandó el cliente (null en lo cargado desde la web de
   * escritorio o por un backend viejo). La carga en vivo lo usa para NO contar
   * dos veces un evento que ya entró pero cuya respuesta se perdió.
   */
  clientEventId?: string | null;
};

export async function fetchMatchEvents(
  matchId: string,
): Promise<{ events: MatchEventRow[]; clock?: RelojPartido | null }> {
  // `clock`: el reloj oficial del partido (ver relojDelPartido.ts). Opcional:
  // un backend viejo no lo manda y la carga sigue andando, sin minuto solo.
  return apiFetch<{ events: MatchEventRow[]; clock?: RelojPartido | null }>(
    `/api/v1/organizer/matches/${matchId}/events`,
  );
}

export async function addMatchEvent(
  matchId: string,
  body: {
    eventCode: string;
    teamId?: string | null;
    playerId?: string | null;
    /** CAMBIO: quién entra / quién sale (cualquiera puede faltar). */
    playerInId?: string | null;
    playerOutId?: string | null;
    minute?: number | null;
    periodCode?: string | null;
    // Override de la suspensión: alinear al jugador igual (confirmación explícita).
    force?: boolean;
    /** Por qué se lo alinea igual (165): queda en la constancia del partido. */
    forceReason?: string | null;
    /** Idempotencia (051): un reintento con el mismo id devuelve el evento que
     *  ya entró, en vez de cargarlo dos veces. */
    clientEventId?: string | null;
    /** Momento REAL del toque (epoch ms): lo que sale tarde de la cola no queda
     *  con la hora del drenado y el acta respeta el orden de lo que pasó. */
    clientCreatedAtMs?: number | null;
  },
  /** Ver `timeoutMs` en client.ts: sólo con clientEventId (idempotente). */
  opciones?: { timeoutMs?: number },
): Promise<MatchEventRow> {
  return apiFetch<MatchEventRow>(
    `/api/v1/organizer/matches/${matchId}/events`,
    { method: "POST", body, timeoutMs: opciones?.timeoutMs },
  );
}

export type MatchSuspensionRow = { playerId: string; playerName: string; teamId: string; reason: string };

/** Un suspendido que jugó FORZADO en el partido (165): quién, cuándo y por qué. */
export type ForzadoDeSuspension = {
  id: string;
  playerId: string;
  playerName: string;
  eventCode: string;
  sancion: string;
  /** null = lo forzó una versión vieja de la app, que no pedía motivo. */
  motivo: string | null;
  createdByName: string | null;
  createdAt: string;
  /** El evento forzado ya no está en el acta (se borró después). */
  eventoBorrado: boolean;
};

export async function fetchForzadosDeSuspension(matchId: string): Promise<ForzadoDeSuspension[]> {
  const r = await apiFetch<{ forzados: ForzadoDeSuspension[] }>(
    `/api/v1/organizer/matches/${matchId}/suspension-overrides`,
  );
  return r.forzados ?? [];
}

export async function fetchMatchSuspensions(
  matchId: string,
): Promise<{ suspended: MatchSuspensionRow[] }> {
  return apiFetch<{ suspended: MatchSuspensionRow[] }>(
    `/api/v1/organizer/matches/${matchId}/suspensions`,
  );
}

export async function deleteMatchEvent(
  matchId: string,
  eventId: string,
  /** Tope de espera (la cola de vivo): el DELETE es idempotente. */
  opciones?: { timeoutMs?: number },
): Promise<{ deleted: boolean }> {
  return apiFetch(
    `/api/v1/organizer/matches/${matchId}/events/${eventId}`,
    { method: "DELETE", timeoutMs: opciones?.timeoutMs },
  );
}

export type TopScorerRow = {
  rank: number;
  playerId: string | null;
  playerName: string | null;
  photoUrl: string | null;
  /** null = goleador arrastrado del que todavia no se sabe el equipo (157). */
  teamId: string | null;
  teamName: string | null;
  goals: number;
};

/**
 * Goleadores de la TEMPORADA: la misma suma que la tabla anual (torneos
 * propios y compartidos, con acta o arrastrados), por persona. Viaja dentro de
 * la respuesta de goleadores; null = el torneo no suma en ninguna anual.
 */
export type AnnualTopScorers = {
  annualTableId: string;
  name: string;
  tournamentName: string;
  tournaments: Array<{ id: string; name: string }>;
  topScorers: TopScorerRow[];
};

export type TopScorersResponse = { topScorers: TopScorerRow[]; annual?: AnnualTopScorers | null };

export async function fetchTopScorers(
  tournamentId: string,
): Promise<TopScorersResponse> {
  return apiFetch<TopScorersResponse>(
    `/api/v1/organizer/tournaments/${tournamentId}/top-scorers`,
  );
}

export type TopAssistRow = {
  rank: number;
  playerId: string | null;
  playerName: string | null;
  photoUrl: string | null;
  teamId: string;
  teamName: string;
  assists: number;
};

export async function fetchTopAssists(
  tournamentId: string,
): Promise<{ topAssists: TopAssistRow[] }> {
  return apiFetch<{ topAssists: TopAssistRow[] }>(
    `/api/v1/organizer/tournaments/${tournamentId}/top-assists`,
  );
}

export type GoalkeeperPlayer = {
  playerId: string;
  playerName: string;
  jerseyNumber: number | null;
  photoUrl: string | null;
};

export type GoalkeeperRow = {
  rank: number;
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  played: number;
  cleanSheets: number;
  goalsAgainst: number;
  /** GC/PJ con dos decimales: la métrica que ordena el ranking. */
  concededPerMatch: number;
  goalkeepers: GoalkeeperPlayer[];
};

export type TopGoalkeepers = {
  goalkeepers: GoalkeeperRow[];
  /** Jugaron menos del mínimo: se muestran aparte, sin puesto. */
  belowMinimum: Array<Omit<GoalkeeperRow, "rank">>;
  minimumPlayed: number;
  maxPlayed: number;
};

export async function fetchTopGoalkeepers(tournamentId: string): Promise<TopGoalkeepers> {
  return normalizarValla(
    await apiFetch<TopGoalkeepers>(`/api/v1/organizer/tournaments/${tournamentId}/goalkeepers`),
  );
}

/**
 * RED CONTRA EL BACKEND VIEJO. Entre que sale el web y sale el backend hay una
 * ventana de minutos donde la respuesta todavía viene del servidor anterior:
 * sin `concededPerMatch` (y `undefined.toFixed()` rompe la pestaña entera) y,
 * peor, ORDENADA POR VALLAS. Visto en el preview contra prod: la columna
 * "GC/PJ", que dice ser el criterio, salía 4.33 · 4.67 · 3.33 · 3.67 — una
 * tabla que se contradice a sí misma es peor que no tener la columna.
 *
 * Así que acá se rehace lo que el servidor nuevo ya hace: promedio, mínimo de
 * partidos y orden. Es la MISMA regla que `ordenarValla` en
 * `backend/src/modules/platform/fixture.service.ts` — si allá cambia, acá
 * también. Es código con fecha de vencimiento: se puede borrar cuando no queden
 * backends sin `concededPerMatch`.
 */
export function normalizarValla<T extends TopGoalkeepers>(res: T): T {
  const conPromedio = <R extends Omit<GoalkeeperRow, "rank">>(row: R): R => ({
    ...row,
    concededPerMatch:
      row.concededPerMatch ??
      (row.played > 0 ? Math.round((row.goalsAgainst / row.played) * 100) / 100 : 0),
  });
  const goalkeepers = (res.goalkeepers ?? []).map(conPromedio);
  const belowMinimum = (res.belowMinimum ?? []).map(conPromedio);

  // El backend nuevo SIEMPRE manda el promedio: si está, ya vino ordenado.
  const servidorNuevo = (res.goalkeepers ?? []).every((row) => row.concededPerMatch != null);
  if (servidorNuevo) {
    return { ...res, goalkeepers, belowMinimum, minimumPlayed: res.minimumPlayed ?? 0 };
  }

  const maxPlayed = goalkeepers.reduce((max, row) => Math.max(max, row.played), 0);
  const minimumPlayed = Math.max(1, Math.ceil((maxPlayed * 2) / 3));
  const porMerito = (a: Omit<GoalkeeperRow, "rank">, b: Omit<GoalkeeperRow, "rank">) =>
    a.concededPerMatch - b.concededPerMatch ||
    b.played - a.played ||
    b.cleanSheets - a.cleanSheets ||
    a.teamName.localeCompare(b.teamName, "es");

  const califican = goalkeepers.filter((row) => row.played >= minimumPlayed).sort(porMerito);
  const afuera = goalkeepers.filter((row) => row.played < minimumPlayed).sort(porMerito);
  let claveAnterior: string | null = null;
  let rankAnterior = 0;
  const conPuesto = califican.map((row, idx) => {
    const clave = `${row.concededPerMatch}|${row.played}|${row.cleanSheets}`;
    const rank = clave === claveAnterior ? rankAnterior : idx + 1;
    claveAnterior = clave;
    rankAnterior = rank;
    return { ...row, rank };
  });

  return {
    ...res,
    goalkeepers: conPuesto,
    belowMinimum: [...belowMinimum, ...afuera].sort(porMerito),
    minimumPlayed,
    maxPlayed,
  };
}

export type DisciplinaryRow = {
  playerId: string;
  playerName: string;
  photoUrl: string | null;
  teamId: string;
  teamName: string;
  yellows: number;
  reds: number;
  suspensionMatches: number;
};

export type DisciplinaryResponse = {
  config: { yellowCardsForSuspension: number | null; redCardSuspensionMatches: number | null };
  rows: DisciplinaryRow[];
};

export async function fetchDisciplinary(
  tournamentId: string,
): Promise<DisciplinaryResponse> {
  return apiFetch<DisciplinaryResponse>(
    `/api/v1/organizer/tournaments/${tournamentId}/disciplinary`,
  );
}

/**
 * Sanciones del torneo (motor disciplinario). El GET recomputa en el backend a
 * partir de lo parametrizado en el torneo (amarillas para suspensión / fechas
 * por roja), sin pisar lo que el organizador editó o levantó (`overridden`).
 */
export type OrganizerSuspension = {
  id: string;
  playerId: string | null;
  playerName: string;
  photoUrl: string | null;
  teamId: string | null;
  teamName: string;
  reason: string | null;
  status: "active" | "cancelled" | string;
  origin: "automatic" | "manual" | string;
  originKind: string | null;
  matchesCount: number | null;
  startsMatchday: number | null;
  endsMatchday: number | null;
  overridden: boolean;
  /** true si toda la ventana de la sanción ya se jugó (histórica, no vigente). */
  served?: boolean;
  /**
   * Fechas que todavía le faltan cumplir, de las `matchesCount` que le pusieron.
   * null cuando la sanción es manual y no tiene ventana contra la cual
   * descontar (no se sabe, que no es lo mismo que cero).
   */
  remainingMatches?: number | null;
};

export async function fetchSuspensions(
  tournamentId: string,
): Promise<{ suspensions: OrganizerSuspension[] }> {
  return apiFetch<{ suspensions: OrganizerSuspension[] }>(
    `/api/v1/organizer/tournaments/${tournamentId}/suspensions`,
  );
}

export async function createSuspension(
  tournamentId: string,
  body: {
    playerId: string;
    teamId: string;
    matches: number;
    reason: string;
    startsMatchday?: number | null;
  },
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/api/v1/organizer/tournaments/${tournamentId}/suspensions`,
    { method: "POST", body },
  );
}

/** Decisión final del organizador: levantar (status=cancelled), reactivar o editar fechas/motivo. */
export async function updateSuspension(
  suspensionId: string,
  body: { status?: "active" | "cancelled"; matches?: number; reason?: string | null },
): Promise<{ updated: boolean }> {
  return apiFetch<{ updated: boolean }>(
    `/api/v1/organizer/suspensions/${suspensionId}`,
    { method: "PUT", body },
  );
}

export type VenueRow = {
  id: string;
  name: string;
  address: string | null;
  surfaceType: string | null;
  status: string;
};

export async function fetchLeagueVenues(
  leagueId: string,
): Promise<{ venues: VenueRow[] }> {
  return apiFetch<{ venues: VenueRow[] }>(
    `/api/v1/organizer/leagues/${leagueId}/venues`,
  );
}

export type OcupacionSlot = {
  /** HH:MM, hora de pared (convención scheduled_at: nunca convertir zona). */
  time: string;
  venueId: string;
  venueName: string | null;
  matchId: string;
  tournamentId: string;
  tournamentName: string;
  homeTeamName: string | null;
  awayTeamName: string | null;
  status: string;
};

/** Ocupación de canchas de la liga en un día: qué partido (de cualquier
 *  torneo) toma cada (hora, cancha). Para avisar choques al programar a mano. */
export async function fetchLeagueOcupacion(
  leagueId: string,
  date: string,
): Promise<{ date: string; slots: OcupacionSlot[] }> {
  return apiFetch<{ date: string; slots: OcupacionSlot[] }>(
    `/api/v1/organizer/leagues/${leagueId}/ocupacion?date=${encodeURIComponent(date)}`,
  );
}

export async function createLeagueVenue(
  leagueId: string,
  body: { name: string; address?: string | null },
): Promise<VenueRow> {
  return apiFetch<VenueRow>(
    `/api/v1/organizer/leagues/${leagueId}/venues`,
    { method: "POST", body },
  );
}

// ============ PLAYERS (Sprint 4) ============

export type TeamPlayerRow = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  positionCode: string | null;
  jerseyNumber: number | null;
  /** "YYYY-MM-DD" de la FICHA (no de la cuenta). null = no se cargó. */
  birthDate: string | null;
  roleInTeam: string;
  photoUrl: string | null;
  // Foto de plano medio con fondo removido (PNG transparente). Se usa en la
  // tarjeta compartible del jugador. NULL = todavía no cargó una (el fallback
  // en la card es el avatar).
  cutoutUrl: string | null;
  /**
   * Foto ORIGINAL (con fondo) de la que salió el recorte. NULL en las fotos
   * cargadas antes de la migración 068. Rehacer el recorte desde acá evita
   * encadenar compresiones con pérdida sobre la imagen ya derivada.
   */
  cutoutSourceUrl: string | null;
  /**
   * ISO timestamp cuando el worker corrió sobre la foto y persistió el PNG
   * transparente. NULL = pendiente de recortar (foto original recién subida).
   * Se usa para saber cuántas fotos quedan por procesar en el batch del equipo.
   */
  cutoutProcessedAt: string | null;
  documentNumber: string | null;
  // Cuenta de app vinculada a la ficha (si hay). Para corregir asociaciones.
  linkedUserId: string | null;
  linkedUserName: string | null;
};

export async function fetchTeamPlayers(
  tournamentId: string,
  teamId: string,
): Promise<{
  players: TeamPlayerRow[];
  coach?: import("./staff").StaffMember | null;
  coaches?: import("./staff").StaffMember[];
  coachFicha?: TeamPlayerRow | null;
  coachFichas?: TeamPlayerRow[];
}> {
  // coachFichas: las fichas completas de los DT que NO juegan (no vienen en
  // `players`), para editarles datos y foto desde el mismo plantel. `coach` y
  // `coachFicha` son el 1er DT, y quedan por los clientes viejos.
  return apiFetch<{
  players: TeamPlayerRow[];
  coach?: import("./staff").StaffMember | null;
  coaches?: import("./staff").StaffMember[];
  coachFicha?: TeamPlayerRow | null;
  coachFichas?: TeamPlayerRow[];
}>(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players`,
  );
}

/** Foto cargada que todavía no pasó por el recorte de fondo. */
export type PendingCutoutRow = {
  playerId: string;
  teamId: string;
  displayName: string;
  cutoutUrl: string;
  /**
   * Nadie encuadró el avatar de listas de este jugador: o no tiene foto, o su
   * foto ES la que vamos a recortar (banco de fotos). Con esto en true, el
   * worker aprovecha la máscara del recorte para encuadrarle la cabeza.
   */
  avatarSinEncuadrar?: boolean;
};

/**
 * Fotos pendientes de recorte en TODO el torneo (no sólo el equipo abierto).
 * La usa el drenado en segundo plano: ver `useTournamentCutoutDrain`.
 */
export async function fetchPendingCutouts(
  tournamentId: string,
): Promise<{ pending: PendingCutoutRow[] }> {
  return apiFetch<{ pending: PendingCutoutRow[] }>(
    `/api/v1/organizer/tournaments/${tournamentId}/pending-cutouts`,
  );
}

/**
 * Recortes ya hechos a los que les falta el encuadre de la tarjeta. Red de
 * seguridad: el encuadre normalmente lo calcula el worker al terminar el
 * recorte, pero ese camino corre en el navegador y puede cortarse sin que nadie
 * se entere. Ver `useTournamentCutoutDrain`.
 */
export async function fetchPendingCutoutFrames(
  tournamentId: string,
): Promise<{ pending: Array<{ playerId: string; displayName: string; cutoutUrl: string }> }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/pending-cutout-frames`);
}

/**
 * Recortes ya hechos cuyo avatar de listas sigue siendo la foto entera: el
 * paso del avatar se cayó en el navegador que hizo el recorte. El drenado lo
 * completa desde el recorte guardado. Ver `useTournamentCutoutDrain`.
 */
export async function fetchPendingAvatars(
  tournamentId: string,
): Promise<{ pending: Array<{ playerId: string; teamId: string; displayName: string; cutoutUrl: string }> }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/pending-avatars`);
}

/**
 * Encuadres vigentes SIN señales de revisión (escritos antes de que existieran).
 * El drenado las completa sin tocar el encuadre. Ver useTournamentCutoutDrain.
 */
export async function fetchPendingCutoutSignals(
  tournamentId: string,
): Promise<{ pending: Array<{ playerId: string; displayName: string; cutoutUrl: string; frame: CutoutFrame }> }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/pending-cutout-signals`);
}

/** Guarda el encuadre de la tarjeta. El backend lo ata al recorte actual. */
export async function setPlayerCutoutFrame(
  playerId: string,
  frame: CutoutFrame | null,
): Promise<{ cutoutFrame: CutoutFrame | null }> {
  return apiFetch(`/api/v1/league-cards/players/${playerId}/frame`, {
    method: "PUT",
    body: { frame },
  });
}

export async function addTeamPlayers(
  tournamentId: string,
  teamId: string,
  players: Array<{
    firstName: string;
    lastName: string;
    positionCode?: string | null;
    jerseyNumber?: number | null;
    documentNumber?: string | null;
    /** "YYYY-MM-DD" de la FICHA: lo pide el control de menores en cancha. */
    birthDate?: string | null;
    /**
     * Homónimo SIN documento que dirima: el backend rebota con
     * `name_match_needs_decision` y la UI pregunta. "same_person" reusa la
     * ficha existente, "new_person" crea una aparte. Decisión POR jugador.
     */
    nameMatchDecision?: "same_person" | "new_person" | null;
  }>,
  /**
   * El jugador ya juega en OTRO equipo del torneo. Sin esto el backend rebota
   * con `player_in_other_team`: el cliente avisa fuerte y reintenta con true.
   */
  confirmCrossTeam?: boolean,
  /** Pasar por encima del cupo o del cierre de pases. Sólo el organizador. */
  overrideRosterLimits?: boolean,
): Promise<{ created: number; reused: number; associated: number }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players`,
    { method: "POST", body: { players, confirmCrossTeam, overrideRosterLimits } },
  );
}

export async function updateTeamPlayer(
  tournamentId: string,
  teamId: string,
  playerId: string,
  body: {
    // PATCH parcial: todos opcionales. El flujo async del cutout manda SOLO
    // campos de foto para no pisar ediciones concurrentes (el backend valida
    // nombre solo si viene).
    firstName?: string;
    lastName?: string;
    positionCode?: string | null;
    jerseyNumber?: number | null;
    /** "YYYY-MM-DD"; null la borra. Dato de la FICHA, no de la cuenta. */
    birthDate?: string | null;
    photoAssetId?: string | null;
    /**
     * Avatar encuadrado solo desde la máscara del recorte. El backend lo aplica
     * SÓLO si nadie encuadró un avatar a mano (vuelve a chequearlo al escribir).
     */
    autoAvatarAssetId?: string;
    cutoutAssetId?: string | null;
    /** Foto original del recorte (migración 068). */
    cutoutSourceAssetId?: string | null;
    /** El original EXACTO de esa foto, ya verificado en Blob (145). */
    originalId?: string;
    /**
     * Marca "ya pasó por el modelo": true = ahora (post-worker); false = NULL
     * (foto original recién subida, pendiente); undefined = no toca.
     */
    cutoutProcessed?: boolean;
    /**
     * Encuadre calculado solo al terminar el recorte (migración 128). El backend
     * lo ata al `cutoutAssetId` de este mismo PATCH y no lo escribe sin él.
     */
    autoCutoutFrame?: CutoutFrame;
    /** LEGACY. Nuevos clientes usan roleInTeam. Si se manda roleInTeam prevalece. */
    isCaptain?: boolean;
    /** Canónico. Capitán y delegado son únicos por equipo/torneo. */
    roleInTeam?: "player" | "captain" | "delegate";
    documentNumber?: string | null;
  },
): Promise<{ updated: boolean }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players/${playerId}`,
    { method: "PUT", body },
  );
}

export type TeamPlayerRemovalImpact = {
  matchesPlayed: number;
  goals: number;
  yellows: number;
  reds: number;
  upcomingRoster: number;
  hasHistory: boolean;
};

/** Qué se lleva puesto quitar a este jugador (alimenta la confirmación). */
export async function fetchTeamPlayerRemovalImpact(
  tournamentId: string,
  teamId: string,
  playerId: string,
): Promise<TeamPlayerRemovalImpact> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players/${playerId}/removal-impact`,
  );
}

export async function removeTeamPlayer(
  tournamentId: string,
  teamId: string,
  playerId: string,
  confirmHistory = false,
): Promise<{ removed: boolean }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players/${playerId}${
      confirmHistory ? "?confirmHistory=true" : ""
    }`,
    { method: "DELETE" },
  );
}

/** Desvincula la cuenta de app de una ficha (corrige asociaciones equivocadas). */
export async function unlinkPlayerAccount(
  tournamentId: string,
  teamId: string,
  playerId: string,
): Promise<{ unlinked: boolean; freedUserId: string | null }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players/${playerId}/unlink-user`,
    { method: "POST" },
  );
}

/** Unir dos fichas del mismo plantel que son la misma persona. `conservar`
 *  se queda con nombre, foto e historial; `absorber` desaparece. */
export async function mergeTeamPlayers(
  tournamentId: string,
  teamId: string,
  conservar: string,
  absorber: string,
): Promise<{ merged: true }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/players/merge`, {
    method: "POST",
    body: { conservar, absorber },
  });
}

// ============ VENUES (gestión) ============

export type VenueManageRow = VenueRow & { matchCount: number };

export async function fetchLeagueVenuesForManagement(
  leagueId: string,
): Promise<{ venues: VenueManageRow[] }> {
  return apiFetch<{ venues: VenueManageRow[] }>(
    `/api/v1/organizer/leagues/${leagueId}/venues/manage`,
  );
}

export async function updateLeagueVenue(
  leagueId: string,
  venueId: string,
  body: { name?: string; address?: string | null; status?: "active" | "inactive" },
): Promise<{ updated: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/venues/${venueId}`, {
    method: "PUT",
    body,
  });
}

export async function deleteLeagueVenue(
  leagueId: string,
  venueId: string,
): Promise<{ deleted: boolean; deactivated: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/venues/${venueId}`, {
    method: "DELETE",
  });
}

// ============ CADENA DE ASCENSOS/DESCENSOS (migración 060) ============
// Reemplaza el modelo previo de divisionales a nivel liga por cadenas
// explícitas entre torneos: cada torneo elige a qué torneo destino
// ascienden sus primeros N y descienden sus últimos M. Soporta ramas
// paralelas (+33 Sábados vs +33 Domingos) sin concepto extra.

export type TournamentChainRow = {
  tournamentId: string;
  name: string;
  season: string;
  status: string;
  ageCategoryLabel: string | null;
  /** Competencia (102) del torneo destino: evita cruzar campeonatos. */
  competitionId: string | null;
  competitionName: string | null;
  promoteTopN: number | null;
  relegateBottomN: number | null;
  promotesToTournamentId: string | null;
  promotesToTournamentName: string | null;
  relegatesToTournamentId: string | null;
  relegatesToTournamentName: string | null;
};

export type TournamentPromotionMovement = {
  teamId: string;
  teamName: string;
  fromTournamentId: string;
  fromTournamentName: string;
  toTournamentId: string;
  toTournamentName: string;
  fromPosition: number;
  kind: "promotion" | "relegation";
};

export type TournamentPromotionPreview = {
  tournamentId: string;
  tournamentName: string;
  ready: boolean;
  blockers: string[];
  teamCount: number;
  movements: TournamentPromotionMovement[];
  /**
   * De qué tabla salen los movimientos: del torneo, de la Tabla Anual (099) o
   * de la tabla de PROMEDIOS de las últimas temporadas (101).
   */
  rankingSource?: "annual" | "average" | "tournament";
  annualName?: string | null;
};

export async function fetchLeaguePromotionChain(leagueId: string): Promise<TournamentChainRow[]> {
  return apiFetch<TournamentChainRow[]>(`/api/v1/organizer/leagues/${leagueId}/promotion-chain`);
}

export async function fetchTournamentPromotionPreview(tournamentId: string): Promise<TournamentPromotionPreview> {
  return apiFetch<TournamentPromotionPreview>(
    `/api/v1/organizer/tournaments/${tournamentId}/promotion-preview`,
  );
}

export async function applyTournamentPromotion(
  tournamentId: string,
): Promise<{ tournamentId: string; tournamentName: string; inserted: number; skipped: number; movements: TournamentPromotionMovement[] }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/promotion-apply`, { method: "POST" });
}

export async function createNextEdition(
  tournamentId: string,
  body: { nextSeason: string; nextName?: string | null },
): Promise<{ id: string; name: string; season: string; linkedFrom: string; linkAs: "promotion" | "relegation" | "none" }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/next-edition`, { method: "POST", body });
}

/**
 * Duplica un torneo heredando TODA su config (formato, puntaje, modalidad,
 * categoría, disciplina, programación). Se usa para el flujo "crear torneo
 * destino inline" desde Nuevo Torneo: si estoy creando "Segunda 2026" y
 * necesito "Primera 2026" como destino, duplico Segunda con `linkAs:"none"`
 * y le cambio el nombre. El linkeo lo hace el createTournament principal
 * cuando reciba `promotesToTournamentId` con el id del recién creado.
 */
export async function duplicateTournament(
  sourceTournamentId: string,
  body: {
    nextSeason?: string | null;
    nextName?: string | null;
    linkAs?: "promotion" | "relegation" | "none";
    /** Inscribe los mismos equipos (el Clausura que sigue al Apertura). */
    copyTeams?: boolean;
    /** Los deja sumando en la misma Tabla Anual (099). */
    joinAnnual?: boolean;
    /** El Intermedio: dos series, formato por grupos. */
    groupCount?: number | null;
    formatCode?: "round_robin" | "groups_playoffs" | "knockout" | null;
  },
): Promise<{
  id: string;
  name: string;
  season: string;
  linkedFrom: string;
  linkAs: "promotion" | "relegation" | "none";
  teamsCopied: number;
  /** Jugadores de esos equipos (los planteles son por torneo). */
  playersCopied: number;
  annualTableId: string | null;
}> {
  return apiFetch(`/api/v1/organizer/tournaments/${sourceTournamentId}/duplicate`, { method: "POST", body });
}

// ============ EQUIPO RETIRADO ============

export type WithdrawPendingPolicy = "cancel" | "walkover";
/** Que pasa con los partidos que el retirado YA jugo. */
export type WithdrawPlayedPolicy = "respect" | "annul";

export type WithdrawPreview = {
  teamName: string;
  playedMatches: number;
  pendingMatches: number;
  /** W.O. que hay que CREAR porque el cruce todavia no existe. */
  missingWalkovers: number;
  beneficiaries: Array<{ teamId: string; teamName: string; walkovers: number }>;
  /** Ventaja de diferencia de gol que queda en pie por haberle ganado de verdad. */
  goalDifferenceSkew: Array<{ teamId: string; teamName: string; goalDifference: number }>;
  remainingTeams: number;
};

/** Que va a pasar si retiro este equipo. No escribe nada. */
export async function previewWithdrawTeam(
  tournamentId: string,
  teamId: string,
  params: {
    pendingPolicy?: WithdrawPendingPolicy;
    playedPolicy?: WithdrawPlayedPolicy;
    walkoverScore?: number;
  },
): Promise<WithdrawPreview> {
  const qs = new URLSearchParams();
  if (params.pendingPolicy) qs.set("pendingPolicy", params.pendingPolicy);
  if (params.playedPolicy) qs.set("playedPolicy", params.playedPolicy);
  if (params.walkoverScore != null) qs.set("walkoverScore", String(params.walkoverScore));
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/withdraw/preview?${qs}`,
  );
}

export async function withdrawTeamFromTournament(
  tournamentId: string,
  teamId: string,
  body: {
    reason: string | null;
    pendingPolicy?: WithdrawPendingPolicy;
    playedPolicy?: WithdrawPlayedPolicy;
    walkoverScore?: number;
  },
): Promise<{
  withdrawn: boolean;
  pendingPolicy: WithdrawPendingPolicy;
  playedPolicy: WithdrawPlayedPolicy;
  resolvedMatches: number;
  keptPlayedMatches: number;
  annulledPlayedMatches: number;
  /** W.O. creados porque el cruce no existia. El aviso los cuenta. */
  settledMatches: number;
}> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/withdraw`,
    { method: "POST", body },
  );
}

export async function updateLeagueSettings(
  leagueId: string,
  body: {
    name?: string;
    logoAssetId?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    isPublic?: boolean;
    /** El país, como su zona horaria IANA: el backend deduce `country_code`. */
    timezone?: string;
  },
): Promise<{ updated: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/settings`, {
    method: "PUT",
    body,
  });
}

/** La liga con su zona horaria y su país (GET /organizer/leagues/:id). */
export async function fetchLeagueBasics(
  leagueId: string,
): Promise<{ id: string; name: string; timezone: string; countryCode: string | null }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}`);
}

// Partido suelto (amistoso de torneo / desempate): crea un partido real que
// SUMA a la tabla (stage_code='group'). tournamentId manda; tournamentName es
// el fallback histórico de mobile.
export async function createQuickMatch(body: {
  tournamentId: string;
  tournamentName: string;
  matchdayLabel: string;
  homeTeamName: string;
  awayTeamName: string;
  dateLabel: string;
  timeLabel: string;
  venueLabel: string;
  scheduledAt?: string | null;
  /**
   * 'bye' = amistoso entre los LIBRES de la fecha (grupos impares): partido
   * real con cancha y hora; la tabla lo acredita según byeMode.
   */
  kind?: "group" | "bye";
  /** Regla del amistoso: 'double_win' (ambos ganan 3-0, default) o 'real_result'. */
  byeMode?: "double_win" | "real_result";
}): Promise<{ id: string }> {
  return apiFetch(`/api/v1/organizer/matches/quick`, { method: "POST", body });
}

export async function updateMatchSchedule(
  matchId: string,
  body: {
    dateLabel: string;
    timeLabel: string;
    venueLabel: string;
    // ISO de la fecha/hora real elegida. Sin esto el backend sólo movía el
    // label de display y NO el scheduled_at real (bug de reprogramación).
    // AUSENTE = no tocar; `null` EXPLÍCITO = desprogramar el partido.
    scheduledAt?: string | null;
    // Cancha por id. Manda sobre venueLabel y es la única forma de SACARLA
    // (mandando null). Ausente = no tocar.
    venueId?: string | null;
  },
): Promise<unknown> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/schedule`, {
    method: "PUT",
    body,
  });
}

/**
 * Edita el cruce de un partido no jugado: cambiar rival, invertir localía
 * (mandando home/away cruzados) y/o mover de jornada.
 *
 * Si el cruce resultante ya existe en otra jornada, el backend rebota con
 * 409 `duplicate_pairing` (details: conflictMatchId, conflictMatchdayName).
 * Reintentar el MISMO PUT con `onDuplicate: "swap"` intercambia los cruces
 * de forma transaccional; la respuesta trae `swappedWith`.
 */
export async function updateMatchPairing(
  matchId: string,
  body: {
    homeTeamId?: string;
    awayTeamId?: string;
    matchdayId?: string | null;
    onDuplicate?: "swap";
  },
): Promise<{ updated: boolean; swappedWith?: { matchId: string; matchdayName: string | null } }> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/pairing`, {
    method: "PUT",
    body,
  });
}

export type MoveMatchesTarget =
  | { kind: "existing"; matchdayId: string }
  | { kind: "new"; name?: string }
  | { kind: "special"; label: "Pendientes" | "Recuperación" };

/**
 * Mueve partidos a otra jornada (existente / nueva / especial), de forma
 * controlada. No cambia equipos ni toca resultados/tabla/bracket.
 */
export async function moveMatches(
  tournamentId: string,
  body: {
    matchIds: string[];
    target: MoveMatchesTarget;
    clearSchedule?: boolean;
    reason?: string | null;
  },
): Promise<{ moved: number; matchdayId: string; matchdayName: string; warnings: string[] }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/matches/move`, {
    method: "POST",
    body,
  });
}

/**
 * Mueve el CORTE de publicación del fixture.
 *
 * Ocultar la fecha N oculta N y las siguientes; publicarla publica N y las
 * anteriores. No es un interruptor por fecha: un agujero en el medio le haría
 * creer al jugador que su próximo partido es el de más adelante.
 */
export async function setMatchdayVisibility(
  tournamentId: string,
  matchdayId: string,
  hidden: boolean,
): Promise<{
  matchdayId: string;
  hidden: boolean;
  matchdaysAffected: number;
  matchesAffected: number;
  firstHiddenLabel: string | null;
}> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/matchdays/${matchdayId}/visibility`,
    { method: "PUT", body: { hidden } },
  );
}

/** Fija ("clava") o libera un partido para que la reorganización no lo mueva. */
export async function setMatchLock(matchId: string, locked: boolean): Promise<{ locked: boolean }> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/lock`, { method: "PUT", body: { locked } });
}

/**
 * Reorganiza las fechas restantes (reparto parejo; respeta jugados y fijados).
 * Opcional: `pinMatchIds` fija esos partidos en `pinTargetMatchdayId` (fecha
 * existente, o null = nueva al final) y reparte el resto alrededor;
 * `reschedule` reprograma horarios/canchas con equilibrio (contempla historial).
 */
export async function reorganizeRemainingMatchdays(
  tournamentId: string,
  opts?: { pinMatchIds?: string[]; pinTargetMatchdayId?: string | null; reschedule?: boolean },
): Promise<{ moved: number; matchdaysCreated: number; rescheduled: number; warnings: string[] }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/fixture/reorganize`, {
    method: "POST",
    body: opts ?? {},
  });
}

/**
 * Posterga una fecha entera N días conservando horarios/canchas. Si `cascade`,
 * corre también todas las fechas siguientes (para no pisar el calendario).
 */
export async function postponeMatchday(
  tournamentId: string,
  matchdayId: string,
  body: { days: number; cascade: boolean },
): Promise<{ shifted: number; matchdaysAffected: number; warnings: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/matchdays/${matchdayId}/postpone`,
    { method: "POST", body },
  );
}

/**
 * Cambia el estado administrativo de un partido (suspender / postergar / no
 * presentado / cancelar). Recalcula la tabla en el backend.
 */
export async function updateMatchAdministration(
  matchId: string,
  body: {
    status: "suspended" | "postponed" | "walkover" | "cancelled";
    notes?: string | null;
  },
): Promise<{ id: string; status: string }> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/administration`, {
    method: "PUT",
    body,
  });
}

/**
 * Comentario del partido que VE EL JUGADOR ("se juega en la cancha 2",
 * "suspendido por lluvia, se reprograma"). Cadena vacía = se borra.
 *
 * Es un campo distinto de la nota administrativa (`notes`): esa la escribe
 * también el sistema y no se publica.
 */
export async function setMatchPublicNote(
  matchId: string,
  publicNote: string | null,
): Promise<{ id: string; status: string }> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}`, {
    method: "PUT",
    body: { publicNote },
  });
}

/**
 * Endpoint nuevo de Sprint 2: crea equipos en la liga (si no existen)
 * Y los asocia al torneo en una sola llamada. Es lo que el panel web
 * usa desde TeamsTab.
 */
export async function addTeamsToTournament(
  tournamentId: string,
  teams: Array<{ name: string; shortName?: string }>,
): Promise<AddTeamsToTournamentResult> {
  return apiFetch<AddTeamsToTournamentResult>(
    `/api/v1/organizer/tournaments/${tournamentId}/teams`,
    { method: "POST", body: { teams } },
  );
}

export async function updateMatchResult(
  matchId: string,
  body: {
    homeScore: number;
    awayScore: number;
    homeScorePenalties?: number | null;
    awayScorePenalties?: number | null;
    status?: "played" | "suspended" | "postponed" | "cancelled";
  },
): Promise<unknown> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/result`, {
    method: "PUT",
    body,
  });
}

export async function generateSimpleFixture(
  tournamentId: string,
  body: { startDate: string; isDoubleRound: boolean; daysBetweenRounds?: number; weekdays?: number[]; startTime?: string },
): Promise<{ roundsCreated: number; matchesCreated: number; byes: number; warnings?: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/generate-simple`,
    { method: "POST", body },
  );
}

export async function generateKnockoutFixture(
  tournamentId: string,
  body: { startDate: string; includeThirdPlace?: boolean; daysBetweenRounds?: number },
): Promise<{ totalRounds: number; matchesFirstRound: number; byes: number; warnings?: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/generate-knockout`,
    { method: "POST", body },
  );
}

// --- Copas por nivel (Oro / Plata / Bronce) --------------------------------
// Al cerrar los grupos, en vez de un cuadro único donde se elimina la mitad, el
// organizador reparte a TODOS en copas paralelas por nivel definiendo rangos de
// posición. Ver docs/design/2026-08-03-copas-por-nivel.md

export type CupFormat = "knockout" | "round_robin";

export type CupTeam = {
  teamId: string;
  teamName: string;
  groupId: string | null;
  groupName: string | null;
  /** Posición con la que terminó la fase de grupos. */
  position: number;
};

export type CupProposal = {
  name: string;
  formatCode: CupFormat;
  fromPosition: number;
  toPosition: number;
  teams: CupTeam[];
};

/** Propuesta de reparto calculada desde las posiciones. No persiste nada. */
export async function fetchCupsProposal(
  tournamentId: string,
): Promise<{
  maxGroupSize: number;
  groupCount: number;
  cups: CupProposal[];
  /**
   * EL PORQUÉ DE ESTE REPARTO, en un renglón, igual que el `criterio` de la
   * fase final. Opcional porque el backend desplegado puede ser anterior a
   * este bundle: sin él la tarjeta de la propuesta sale sin su segundo
   * renglón, que es exactamente como salía antes.
   */
  criterio?: string;
  /**
   * EL DÍA QUE PROPONE FORJA (yyyy-MM-dd): el mismo de la elección. Viaja acá
   * para que el armador cree las copas YA fechadas en una sola llamada, en vez
   * de pedir `/playoffs` entero sólo para saber qué día juega el torneo.
   * Opcional: un backend anterior a este bundle no lo manda y las copas nacen
   * sin día, como antes.
   */
  diaSugerido?: string | null;
}> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/cups/proposal`);
}

export type CupRow = {
  id: string;
  name: string;
  sortOrder: number;
  formatCode: CupFormat;
  fromPosition: number | null;
  toPosition: number | null;
  teams: { teamId: string; teamName: string; seed: number }[];
};

/**
 * Corregir el reparto de un torneo que YA tiene copas: mismo cuerpo que crear.
 * El backend valida lo nuevo antes de borrar lo viejo, así un reparto que no
 * sirve no deja al torneo sin copas. Ver `replaceCups`.
 */
export async function replaceCups(
  tournamentId: string,
  cups: {
    name: string;
    formatCode: CupFormat;
    fromPosition?: number | null;
    toPosition?: number | null;
    teamIds: string[];
  }[],
  programacion: ProgramacionDeCopas = {},
): Promise<RespuestaDeCopas> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/cups`, {
    method: "PUT",
    body: { cups, ...programacion },
  });
}

export async function fetchCups(tournamentId: string): Promise<{ cups: CupRow[] }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/cups`);
}

/** Una jornada recién creada: espejo de `JornadaDeCopaCreada` del backend. */
export type JornadaDeCopaCreada = {
  matchdayId: string;
  cupName: string;
  /** 1 para el cuadro; 1..n para cada fecha de la liguilla. */
  roundNumber: number;
};

/**
 * Confirma el armado. `teamIds` es la fuente de verdad (incluye los ajustes
 * manuales); el rango va sólo como referencia de la regla que se usó.
 */
/**
 * EL DÍA, EN LA MISMA LLAMADA QUE CREA LAS COPAS. Con `startDate` las jornadas
 * nacen fechadas y el servidor les pone hora y cancha con la Programación
 * guardada del torneo (Dom · 19/20/21 · 3 canchas), que es el mismo cierre que
 * ya corre la llave. Sin él las copas nacen sin día, como antes.
 */
export type ProgramacionDeCopas = {
  /** yyyy-MM-dd: arranque de la PRIMERA fecha de todas las copas. */
  startDate?: string | null;
  /** Cada cuántos días cae la fecha siguiente de una liguilla. */
  daysBetweenRounds?: number | null;
};

export type RespuestaDeCopas = {
  created: number;
  matchesCreated: number;
  matchdayIds?: string[];
  jornadas?: JornadaDeCopaCreada[];
  /** Partidos a los que el torneo les puso hora y cancha solo. */
  programados?: number;
  warnings?: string[];
};

export async function createCups(
  tournamentId: string,
  cups: {
    name: string;
    formatCode: CupFormat;
    fromPosition?: number | null;
    toPosition?: number | null;
    teamIds: string[];
  }[],
  programacion: ProgramacionDeCopas = {},
): Promise<RespuestaDeCopas> {
  // `jornadas`: las que quedaron creadas, con su copa y su número de fecha. El
  // número de fecha importa porque una LIGUILLA crea una jornada POR FECHA y
  // las tres no se juegan el mismo domingo. `jornadas`, `programados` y
  // `warnings` son opcionales porque el backend desplegado puede ser anterior
  // a este bundle.
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/cups`, {
    method: "POST",
    body: { cups, ...programacion },
  });
}

/** Deshace el armado. Sólo mientras no haya resultados cargados. */
export async function deleteCups(tournamentId: string): Promise<{ deleted: number }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/cups`, { method: "DELETE" });
}

// --- Fase final a mano -----------------------------------------------------
// La tercera forma de resolver lo que sigue a los grupos, y la única que no
// supone un reglamento: el organizador arma cada cruce. Ver
// backend/src/modules/platform/fase-final.service.ts

export const TIPOS_DE_RONDA = [
  "round_of_64",
  "round_of_32",
  "round_of_16",
  "quarterfinal",
  "semifinal",
  "third_place",
  "final",
  "playoff",
] as const;

export type TipoDeRonda = (typeof TIPOS_DE_RONDA)[number];

/** Nombres en castellano. Los mismos que usa el backend (NOMBRE_DE_TIPO). */
export const NOMBRE_DE_TIPO: Record<TipoDeRonda, string> = {
  round_of_64: "Treintaidosavos de final",
  round_of_32: "Dieciseisavos de final",
  round_of_16: "Octavos de final",
  quarterfinal: "Cuartos de final",
  semifinal: "Semifinal",
  third_place: "Tercer puesto",
  final: "Final",
  playoff: "Otra ronda",
};

/** El tipo que FORJA propone por la cantidad de cruces (igual que el backend). */
export function tipoPorCantidad(cruces: number): TipoDeRonda {
  if (cruces === 1) return "final";
  if (cruces === 2) return "semifinal";
  if (cruces === 4) return "quarterfinal";
  if (cruces === 8) return "round_of_16";
  if (cruces === 16) return "round_of_32";
  if (cruces === 32) return "round_of_64";
  return "playoff";
}

export type CandidatoFaseFinal = {
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  groupName: string | null;
  position: number | null;
  /** "1º Grupo A", o "1º" cuando el torneo no tiene grupos. */
  label: string;
  yaJuegaEn: string[];
};

export type PartidoDeRonda = {
  matchId: string;
  homeTeamId: string;
  homeTeamName: string;
  awayTeamId: string;
  awayTeamName: string;
  status: string;
  scheduledAt: string | null;
  venueName: string | null;
  venueId: string | null;
  /** Fijado a mano: programar la ronda entera NO lo toca. */
  bloqueado: boolean;
};

export type RondaFaseFinal = {
  matchdayId: string;
  name: string;
  /** El día de la ronda (YYYY-MM-DD): vive en la JORNADA, no en el partido. */
  dia: string | null;
  stageCode: string;
  tipoLabel: string;
  sequenceNumber: number;
  matches: PartidoDeRonda[];
  sePuedeBorrar: boolean;
  motivoNoBorrar: string | null;
  /**
   * LA RONDA QUE QUEDÓ ABIERTA: quiénes no están en ningún cruce y qué haría
   * FORJA con ellos. La ronda se puede guardar con UN cruce —el día que se
   * arma la fase final, muchas veces las otras posiciones no están definidas—
   * y el resto se suma después.
   *
   * No hay nada que preguntar: los que faltan salen de los candidatos y el
   * cruce de la misma siembra que propone la ronda nueva. `?? null` en quien
   * lo lea: un backend anterior no lo manda.
   */
  completar?: CompletarRonda | null;
};

/**
 * UNA FECHA DE UNA COPA, con sus cruces. Es la unidad que se programa: el
 * endpoint trabaja por JORNADA, y una copa en liguilla tiene varias. Cumple
 * `JornadaProgramable`, así que la programa el MISMO panel que una ronda.
 */
export type FechaDeCopa = {
  matchdayId: string;
  /** Como se llama la jornada: "Copa de Oro · Fecha 2". */
  name: string;
  /** Qué número de fecha de ESTA copa es. 1 para el cuadro de eliminación. */
  roundNumber: number;
  dia: string | null;
  sequenceNumber: number;
  matches: PartidoDeRonda[];
};

/** Una copa por nivel ya armada (Oro, Plata, Bronce), con sus fechas. */
export type CopaArmada = {
  cupId: string;
  name: string;
  sortOrder: number;
  /** "knockout" (eliminación) o "round_robin" (liguilla). */
  formatCode: CupFormat;
  fechas: FechaDeCopa[];
};

export type CompletarRonda = {
  /** Los que no juegan en ninguna ronda, ordenados por puesto. */
  faltan: CandidatoFaseFinal[];
  /** Lo que proponemos para ellos. Vacío cuando queda uno solo: no hay cruce. */
  pairings: { homeTeamId: string; awayTeamId: string }[];
  /** El porqué, en una línea, igual que en la propuesta de la ronda nueva. */
  criterio: string;
};

export type PropuestaRonda = {
  name: string;
  stageCode: TipoDeRonda;
  pairings: { homeTeamId: string; awayTeamId: string }[];
  /**
   * Los clasificados que NO juegan esta ronda y siguen vivos (número impar de
   * equipos). Viaja desde el backend porque no se puede deducir mirando los
   * cruces: un equipo que no aparece en ninguno es indistinguible de uno
   * eliminado. `?? []` en quien lo lea: un backend anterior no lo manda.
   */
  esperan?: { teamId: string; teamName: string }[];
  /**
   * EL CRITERIO, EN UNA LÍNEA Y SIN NOMBRES: el "por qué" que la pantalla
   * muestra siempre. `explicacion` lo dice completo pero vivía en un `title=`,
   * o sea que en un teléfono no existía. `?? ""` en quien lo lea: un backend
   * anterior no lo manda.
   */
  criterio?: string;
  /** Lo mismo pero completo, con los nombres: el detalle para el que lo quiera. */
  explicacion: string;
};

export type FaseFinal = {
  formatCode: string;
  gruposPendientes: number;
  gruposCompletos: boolean;
  hayCopas: boolean;
  bloqueo: string | null;
  /**
   * Qué bloqueo es, para que cada pantalla agregue el DÓNDE que le
   * corresponde: armar y deshacer copas existe SÓLO en el panel de escritorio.
   * `?? null` en quien lo lea: un backend anterior no lo manda.
   */
  bloqueoCodigo?: "cups_already_exist" | "tournament_finished" | null;
  candidatos: CandidatoFaseFinal[];
  rondas: RondaFaseFinal[];
  /**
   * LAS COPAS ARMADAS con sus fechas y sus cruces — lo que `rondas` es para la
   * fase final. Con esto la pantalla de copas ya armadas puede mostrar el día,
   * la hora y la cancha de cada cruce y tener el botón de calendario por
   * fecha, que la fase final tiene desde siempre. Opcional: un backend
   * anterior a este bundle sólo manda `hayCopas`.
   */
  copas?: CopaArmada[];
  propuesta: PropuestaRonda | null;
  /** Las canchas de la liga, para programar la ronda sin salir de la pantalla. */
  canchas: { id: string; name: string }[];
  /**
   * El día que propone FORJA (`yyyy-MM-dd`), o null si el torneo no jugó
   * ninguna fecha con día puesto. Es el día de la semana en que más juega este
   * torneo, en su próxima vuelta. Opcional porque el backend desplegado puede
   * ser anterior a este bundle.
   */
  diaSugerido?: string | null;
};

export async function fetchFaseFinal(tournamentId: string): Promise<FaseFinal> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/playoffs`);
}

/** Crea la ronda con los cruces elegidos. Nace sin día, hora ni cancha. */
export async function crearRondaFaseFinal(
  tournamentId: string,
  ronda: { name: string; stageCode: TipoDeRonda; pairings: { homeTeamId: string; awayTeamId: string }[] },
): Promise<{ matchdayId: string; matchesCreated: number }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/playoffs/rounds`, {
    method: "POST",
    body: ronda,
  });
}

/**
 * Suma cruces a una ronda ya creada: la otra mitad de poder guardarla con uno
 * solo. Entran a la MISMA jornada y con el mismo tipo de ronda, sin día ni
 * cancha — el paso de programar es el de siempre.
 */
export async function agregarCrucesARonda(
  tournamentId: string,
  matchdayId: string,
  pairings: { homeTeamId: string; awayTeamId: string }[],
): Promise<{ matchdayId: string; matchesCreated: number }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/playoffs/rounds/${matchdayId}/pairings`,
    { method: "POST", body: { pairings } },
  );
}

/** Borra una ronda entera. Sólo mientras no haya empezado ningún partido. */
export async function borrarRondaFaseFinal(
  tournamentId: string,
  matchdayId: string,
): Promise<{ deletedMatches: number }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/playoffs/rounds/${matchdayId}`, {
    method: "DELETE",
  });
}

// --- Planillas de una fecha (gestión) --------------------------------------
// Los endpoints de planilla son por partido —lo que sirve en la cancha—. Éste
// resume la fecha entera para que el organizador vea QUÉ FALTA de un vistazo.

export type MatchdayRosterSide = {
  teamId: string;
  teamName: string;
  /** Convocados cargados. 0 = nadie tocó esta planilla. */
  called: number;
  starters: number;
};

export type MatchdayRosterRow = {
  matchId: string;
  status: string;
  scheduledAt: string | null;
  /** El árbitro (o el organizador) ya la confirmó: el capitán no puede editarla. */
  locked: boolean;
  /** Titulares que admite el formato del torneo (11, 8, 5…). */
  maxStarters: number | null;
  home: MatchdayRosterSide;
  away: MatchdayRosterSide;
};

export type RosterEntry = {
  playerId: string;
  displayName: string;
  /** Foto del jugador: el backend ya la manda; sin declararla acá el selector
   *  de jugadores se quedaba con las iniciales. */
  photoUrl: string | null;
  shirtNumber: number | null;
  /** El dorsal viene de la planilla de ESTE partido (no de la ficha). */
  shirtFromRoster: boolean;
  isStarter: boolean;
  inRoster: boolean;
  /** Ya recibió el aviso "estás convocado" para este partido (091). Opcional: backend viejo no lo manda. */
  calledUp?: boolean;
  isCaptain: boolean;
  position: string | null;
  /** Sancionado para este partido: no puede ser titular. */
  unavailable: boolean;
  unavailableReason: string | null;
  unavailableRemaining: number | null;
};

export type TeamRoster = {
  teamId: string;
  teamName: string;
  isLoaded: boolean;
  starterCount: number;
  canEdit: boolean;
  /** Los titulares mostrados son la última alineación del equipo, no ésta. */
  suggestedLineup: boolean;
  /** DT del equipo ESE día (138). No juega: encabezado, nunca entries. */
  coachName?: string | null;
  entries: RosterEntry[];
};

export type MatchRoster = {
  matchId: string;
  tournamentId: string;
  status: string;
  matchFormat: string | null;
  maxStarters: number | null;
  locked: boolean;
  /** El partido arrancó o se resolvió: la planilla quedó fija para todos. */
  matchStarted: boolean;
  /** Fichar desde la planilla: sólo organizador (el backend lo decide). */
  canAddPlayers: boolean;
  /** Cupo de la lista de buena fe y cierre de pases (el veredicto lo da el server). */
  rosterLimits: {
    maxPlayers: number | null;
    lockDate: string | null;
    counts: Record<string, number>;
    transfersClosed: boolean;
  };
  /**
   * De qué lado está el que mira: el equipo del que es jugador, capitán o
   * delegado en este partido. null = ninguno de los dos (organizador, árbitro,
   * espectador). La pantalla ATERRIZA acá: arrancar siempre en "home" le
   * mostraba al capitán del visitante la planilla del rival.
   */
  viewerSide: "home" | "away" | null;
  /**
   * Las dos puertas angostas de EN VIVO (juez u organizador, partido corriendo):
   * sumar un suplente y cambiar un número de camiseta. Ninguna toca la
   * alineación. Distinto de `canEdit` ("puedo armar la planilla").
   */
  canEditLive: boolean;
  home: TeamRoster;
  away: TeamRoster;
};

export async function fetchMatchRoster(matchId: string): Promise<MatchRoster> {
  return apiFetch(`/api/v1/matches/${matchId}/roster`);
}

/** Reemplaza la planilla de un equipo (la lista enviada es la nueva verdad). */
export async function saveTeamRoster(
  matchId: string,
  teamId: string,
  entries: { playerId: string; shirtNumber: number | null; isStarter: boolean }[],
  /** Avisar "estás convocado" (push) a los que aún no lo recibieron. Opt-in explícito. */
  notify = false,
): Promise<unknown> {
  return apiFetch(`/api/v1/matches/${matchId}/teams/${teamId}/roster`, {
    method: "PUT",
    body: { entries, notify },
  });
}

/**
 * SUMAR AL BANCO con el partido en curso: el que llegó tarde y no estaba
 * convocado. Sólo agrega un suplente — no toca a nadie más, que es lo que la
 * hace segura en vivo. Devuelve la planilla del equipo ya fresca.
 */
export async function addPlayerToBench(
  matchId: string,
  teamId: string,
  playerId: string,
  shirtNumber: number | null,
): Promise<TeamRoster> {
  return apiFetch(`/api/v1/matches/${matchId}/teams/${teamId}/roster/bench`, {
    method: "POST",
    body: { playerId, shirtNumber },
  });
}

/**
 * CAMBIAR NÚMEROS con el partido en curso (se cambiaron de camiseta).
 *
 * Se manda una LISTA y no un jugador: en un intercambio (el 5 pasa a la 8 y el
 * 8 a la 5) hacerlo de a uno pasa por un estado con dos en la 5, y el server lo
 * rechaza — bien rechazado. Devuelve la planilla del equipo ya fresca.
 */
export async function setShirtNumbersLive(
  matchId: string,
  teamId: string,
  cambios: Array<{ playerId: string; shirtNumber: number | null }>,
): Promise<TeamRoster> {
  return apiFetch(`/api/v1/matches/${matchId}/teams/${teamId}/roster/shirts`, {
    method: "POST",
    body: { cambios },
  });
}

/** Confirmar (bloquear) o reabrir. Bloqueada, el capitán ya no puede editarla. */
export async function setRosterLock(matchId: string, locked: boolean): Promise<unknown> {
  return apiFetch(`/api/v1/matches/${matchId}/roster/lock`, { method: "POST", body: { locked } });
}

export async function fetchMatchdayRosters(matchdayId: string): Promise<{
  matchdayId: string;
  matchdayName: string | null;
  tournamentId: string;
  matches: MatchdayRosterRow[];
}> {
  return apiFetch(`/api/v1/organizer/matchdays/${matchdayId}/rosters`);
}

// --- Exportar planillas (PDF/Excel para relatores) --------------------------

export type MatchdayRosterExportPlayer = {
  shirtNumber: number | null;
  displayName: string;
  isStarter: boolean;
  position: string | null;
  isCaptain: boolean;
};

export type MatchdayRosterExportSide = {
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  confirmed: boolean;
  /** DT del equipo ese día (138), para el encabezado. */
  coachName?: string | null;
  starters: MatchdayRosterExportPlayer[];
  substitutes: MatchdayRosterExportPlayer[];
};

export type MatchdayRosterExportMatch = {
  matchId: string;
  scheduledAt: string | null;
  venueName: string | null;
  refereeName: string | null;
  home: MatchdayRosterExportSide;
  away: MatchdayRosterExportSide;
};

export type MatchdayRosterExport = {
  matchdayId: string;
  matchdayName: string | null;
  tournamentId: string;
  tournamentName: string | null;
  matches: MatchdayRosterExportMatch[];
};

export async function fetchMatchdayRostersExport(matchdayId: string): Promise<MatchdayRosterExport> {
  return apiFetch(`/api/v1/organizer/matchdays/${matchdayId}/rosters/export`);
}

export async function autoScheduleFixture(
  tournamentId: string,
  // `matchdayIds` acota la corrida a esas jornadas (Programar la fecha).
  // Omitido = todo el torneo, como siempre.
  body: { venueIds: string[]; startTimes: string[]; matchdayIds?: string[] },
): Promise<{ scheduled: number; warnings: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/auto-schedule`,
    { method: "POST", body },
  );
}

/** Crea las fechas que faltan de un round-robin cargado a medias. */
/**
 * UN endpoint, dos trabajos: con partidos COMPLETA los cruces que faltan;
 * sin un solo partido ARMA de cero, delegando en el generador del formato.
 *
 * `startDate` sólo hace falta para armar —es lo único que el backend no
 * puede deducir del torneo, porque los días, horarios y canchas ya están en
 * su Programación—. Completando se ignora: las fechas nuevas cuelgan de las
 * que ya hay.
 */
export async function completeFixture(
  tournamentId: string,
  startDate?: string,
): Promise<{ roundsCreated: number; matchesCreated: number; warnings: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/complete`,
    { method: "POST", body: startDate ? { startDate } : {} },
  );
}

export type RebuildFixtureResult = {
  deletedMatches: number;
  deletedMatchdays: number;
  keptMatches: number;
  roundsCreated: number;
  matchesCreated: number;
  warnings: string[];
  /** true = corrida en seco: los números son lo que PASARÍA, no lo que pasó. */
  dryRun?: boolean;
};

/**
 * Rehace el fixture alrededor de lo fijado/jugado: borra los partidos de fase
 * liga sin candado ni historia y regenera los cruces que faltan. Con `dryRun`
 * no escribe nada y devuelve el mismo conteo — y si no hay nada para rehacer
 * falla con el motivo, igual que la corrida de verdad.
 */
export async function rebuildFixture(
  tournamentId: string,
  body: { dryRun?: boolean } = {},
): Promise<RebuildFixtureResult> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/rebuild`,
    { method: "POST", body },
  );
}

export type ScheduleMatchdayResult = {
  updated: number;
  skippedLocked: number;
  skippedPlayed: number;
  matchdayDate: string | null;
  warnings: string[];
};

/**
 * Guarda la programación de UNA jornada. Por cada partido: `date`/`time`/
 * `venueId` ausentes = no tocar ese dato, `null` explícito = dejarlo sin
 * asignar. `matchdayDate` es el "todos el viernes 3" y se guarda en la jornada,
 * que es donde puede vivir un día sin hora.
 */
export async function scheduleMatchday(
  tournamentId: string,
  matchdayId: string,
  body: {
    entries: Array<{
      matchId: string;
      date?: string | null;
      time?: string | null;
      venueId?: string | null;
    }>;
    matchdayDate?: string | null;
  },
): Promise<ScheduleMatchdayResult> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/matchdays/${matchdayId}/schedule`,
    { method: "PUT", body },
  );
}

export type ClearScheduleResult = {
  affected: number;
  matchdayNames: string[];
  skippedPlayed: number;
  skippedLocked: number;
  alreadyClearDateTime: number;
  alreadyClearVenue: number;
};

/**
 * Deja partidos SIN fecha/hora y/o SIN cancha conservando los cruces: el
 * fixture se genera entero y se programa semana a semana. Con `dryRun` no
 * escribe nada y devuelve el mismo conteo, para la vista previa del modal.
 */
export async function clearFixtureSchedule(
  tournamentId: string,
  body: {
    fromSequence?: number | null;
    onlyThatMatchday?: boolean;
    clearDateTime: boolean;
    clearVenue: boolean;
    dryRun?: boolean;
  },
): Promise<ClearScheduleResult> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/clear-schedule`,
    { method: "POST", body },
  );
}

export type GroupSwapFixture = {
  matchdayName: string;
  sequence: number;
  bye: boolean;
  matchId: string | null;
  opponentName: string | null;
  isHome: boolean | null;
  scheduledAt: string | null;
  venueName: string | null;
  locked: boolean;
};

export type GroupSwapSide = {
  teamId: string;
  teamName: string;
  fromGroupName: string;
  toGroupName: string;
  fixtures: GroupSwapFixture[];
};

export type GroupSwapResult = {
  applied: boolean;
  changedMatches: number;
  teamA: GroupSwapSide;
  teamB: GroupSwapSide;
  warnings: string[];
};

/**
 * Cambia dos equipos de grupo (1 por 1) antes de que jueguen. Con `dryRun`
 * devuelve la vista previa sin tocar nada.
 */
export async function swapGroupTeams(
  tournamentId: string,
  body: { teamAId: string; teamBId: string; dryRun?: boolean },
): Promise<GroupSwapResult> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/groups/swap-teams`, {
    method: "POST",
    body,
  });
}

export async function generateGroupsFixture(
  tournamentId: string,
  body: {
    startDate: string;
    groupCount: number;
    classifyTopN?: number;
    daysBetweenRounds?: number;
    /** Arma las series por la posición en la tabla de otro torneo (Intermedio). */
    seedFromTournamentId?: string | null;
  },
): Promise<{ groupsCreated: number; matchesCreated: number; matchdaysPerGroup: number; warnings?: string[] }> {
  return apiFetch(
    `/api/v1/organizer/tournaments/${tournamentId}/fixture/generate-groups`,
    { method: "POST", body },
  );
}

// ============ CUENTAS DEL TORNEO (libro mayor por equipo) ============

/** Tarifas del torneo. null = ese concepto no se cobra. */
export type TournamentFees = {
  currency: string;
  yellowCard: number | null;
  redCard: number | null;
  walkover: number | null;
  perMatchday: number | null;
  registration: number | null;
  /** Ficha del jugador. Se emite, no se deriva (087). */
  signingFee: number | null;
};

/** Lo que se emitiría al apretar "Emitir cargos de temporada", sin escribir nada. */
export type SeasonChargesPreview = {
  currency: string;
  registrationFee: number | null;
  signingFee: number | null;
  pendingTeams: number;
  pendingPlayers: number;
  issuedTeams: number;
  issuedPlayers: number;
  /** Plata ya emitida, leida del libro. */
  issuedRegistrationAmount: number;
  issuedSigningAmount: number;
  registrationTotal: number;
  signingTotal: number;
  total: number;
  rosterLockDate: string | null;
  rosterOpen: boolean;
};

export async function fetchSeasonChargesPreview(tournamentId: string): Promise<SeasonChargesPreview> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/season-charges/preview`);
}

/** Emite lo que falta. Repetirlo sólo agrega los que entraron después. */
export async function issueSeasonCharges(
  tournamentId: string,
): Promise<{ registrations: number; signings: number; total: number }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/season-charges`, { method: "POST" });
}

export type TeamBalance = {
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  charged: number;
  /** Plata + descuentos: lo que baja el saldo. NO es lo que entró en caja. */
  paid: number;
  /** Plata de verdad. */
  collected: number;
  /** Notas de crédito: bajan la deuda, no son ingreso. */
  discounts: number;
  balance: number;
  /** Se retiró del torneo: sigue debiendo, pero ya no juega. */
  withdrawn: boolean;
  /** Del saldo que debe, cuánto es la cuota de la próxima fecha (se paga antes de jugarla). */
  advanceOwed?: number;
};

export type LedgerEntry = {
  id: string;
  kind: "charge" | "payment";
  concept:
    | "registration"
    | "signing_fee"
    | "matchday_fee"
    | "card_fine"
    | "walkover_fine"
    | "manual"
    | "payment"
    | "adjustment";
  amount: number;
  description: string | null;
  origin: "automatic" | "manual";
  voided: boolean;
  voidedReason: string | null;
  playerId: string | null;
  createdAt: string;
  /** Cuándo pasó el hecho (el partido), no cuándo se escribió la fila. */
  occurredAt: string;
};

export type TeamLedger = {
  currency: string;
  teamId: string;
  teamName: string;
  charged: number;
  paid: number;
  collected: number;
  discounts: number;
  balance: number;
  withdrawn: boolean;
  entries: LedgerEntry[];
};

/** Saldos de todos los equipos. El backend recalcula lo automático al entrar. */
export async function fetchTournamentLedger(tournamentId: string): Promise<{
  currency: string;
  teams: TeamBalance[];
  totals: {
    charged: number;
    paid: number;
    collected: number;
    discounts: number;
    balance: number;
    /** Del saldo: lo que YA se debe. */
    dueNow?: number;
    /** Del saldo: la cuota de la próxima fecha. */
    advanceOwed?: number;
  };
  /** Lo facturado (cargos vivos) abierto por concepto; `detail` = "7 equipos × $1.500". */
  byConcept: Array<{ concept: string; amount: number; count: number; detail?: string }>;
  /** La cuota fecha por fecha: jugada o cobrada por adelantado. */
  byMatchday?: Array<{ label: string; teams: number; amount: number; played: boolean }>;
  /** Inscripción + ficha que falta emitir (todavía no está facturado). */
  pendingSeason?: number;
  fees: TournamentFees;
}> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/ledger`);
}

export async function fetchTeamLedger(tournamentId: string, teamId: string): Promise<TeamLedger> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/ledger`);
}

/** Guarda las tarifas y dispara el recálculo de lo automático. */
export async function setTournamentFees(
  tournamentId: string,
  fees: Partial<TournamentFees>,
): Promise<TournamentFees> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/fees`, { method: "PUT", body: fees });
}

/** Registra un pago, un cargo suelto o un descuento. */
export async function addLedgerEntry(
  tournamentId: string,
  teamId: string,
  body: {
    kind: "charge" | "payment";
    concept: "manual" | "payment" | "adjustment";
    amount: number;
    description?: string | null;
    /** Ticket de idempotencia (089): se genera al abrir el formulario y se reusa si se reintenta. */
    clientEntryId?: string | null;
  },
): Promise<{ id: string; duplicate?: true }> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/teams/${teamId}/ledger`, {
    method: "POST",
    body,
  });
}

/**
 * Ticket para un movimiento del libro. Un UUID por INTENTO de cobro: nace al
 * abrir el formulario y muere al guardar bien. Si se corta la señal y el
 * organizador reintenta, viaja el mismo y el server no cobra dos veces.
 */
export function nuevoTicketDeMovimiento(): string | null {
  const c = globalThis.crypto;
  return c && typeof c.randomUUID === "function" ? c.randomUUID() : null;
}

/** Perdona (o revive) un movimiento. No borra: queda con el motivo. */
export async function voidLedgerEntry(
  entryId: string,
  voided: boolean,
  reason?: string | null,
): Promise<{ id: string; voided: boolean }> {
  return apiFetch(`/api/v1/organizer/ledger/${entryId}/void`, {
    method: "PUT",
    body: { voided, reason },
  });
}

// ============ FAIR PLAY Y DESEMPATE (081) ============

export type FairPlayConfig = {
  yellow: number;
  red: number;
  walkover: number;
  /** ¿El no presentarse suma en Fair Play? Lo elige la liga. */
  countWalkover: boolean;
};

export type FairPlayRow = {
  position: number;
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  yellow: number;
  red: number;
  walkovers: number;
  /** Puntos de amonestación. MENOS es mejor. */
  points: number;
};

export type FairPlayTable = {
  enabled: boolean;
  tournamentName: string;
  /** ¿La tabla desempata por Fair Play? Apagarlo lo saca de ahí también. */
  inTieBreak: boolean;
  config: FairPlayConfig | null;
  rows: FairPlayRow[];
};

/** Los criterios que el motor sabe aplicar, en el orden en que se ofrecen. */
export const CRITERIOS_DESEMPATE = [
  { key: "head_to_head", label: "Enfrentamiento directo" },
  { key: "goal_difference", label: "Diferencia de gol" },
  { key: "goals_for", label: "Goles a favor" },
  { key: "goals_against", label: "Goles en contra" },
  { key: "wins", label: "Partidos ganados" },
  { key: "fair_play", label: "Fair Play" },
] as const;

export async function fetchFairPlay(tournamentId: string): Promise<FairPlayTable> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/fair-play`);
}

/** Guarda el orden de desempate y la config de Fair Play (van juntos). */
export async function setTieBreakConfig(
  tournamentId: string,
  body: { order?: string[]; fairPlay?: FairPlayConfig | null },
): Promise<{
  order: string[];
  fairPlay: FairPlayConfig | null;
  /** Se apagó Fair Play y el server lo sacó también del orden de desempate. */
  removedFromTieBreak: boolean;
}> {
  return apiFetch(`/api/v1/organizer/tournaments/${tournamentId}/tie-break`, {
    method: "PUT",
    body,
  });
}

/**
 * El enlace público legible del torneo. Genera el slug la primera vez que se
 * pide — por eso es el único lugar del sistema que lo asigna.
 */
export async function fetchEnlacePublico(
  tournamentId: string,
): Promise<{ leagueSlug: string; tournamentSlug: string; ruta: string }> {
  return apiFetch(`/organizer/tournaments/${tournamentId}/enlace-publico`);
}

/** Las planillas de la fecha (o de un partido) como archivo, armadas por el servidor. */
export function fetchPlanillasArchivo(matchdayId: string, formato: "pdf" | "xlsx", matchId?: string) {
  return apiArchivo(`/api/v1/organizer/matchdays/${matchdayId}/rosters/planillas.${formato}${matchId ? `?matchId=${matchId}` : ""}`);
}
